import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import ReplayIcon from '@mui/icons-material/Replay';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import TriageBar from './TriageBar';
import { colorFor, initialsFor } from './memberRoles';
import { DECISION_BY_VALUE, describeAge } from './bugDecisions';

/**
 * One bug.
 *
 * What it shows, in order of what a person reading the pool needs: what broke, how
 * long it has been sitting, and what has been decided. Notably absent is anything
 * resembling severity — the decision chip is the only status this carries, because
 * a decision is what a bug in this pool has instead of a priority.
 */
const BugCard = ({
  bug,
  canEdit = false,
  currentUserId = null,
  busy = false,
  onTriage,
  onEmergency,
  onFix,
  onEscalate,
  onClaim,
  onEdit,
  onReportAgain,
  onDelete,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [triageOpen, setTriageOpen] = useState(false);

  const decision = DECISION_BY_VALUE[bug.disposition];
  const isOpen = bug.status === 'open';
  const mine = bug.assignee?.userId && bug.assignee.userId === currentUserId;

  const closeMenu = () => setMenuAnchor(null);
  const act = (fn) => () => { closeMenu(); fn?.(bug); };

  return (
    <Card
      variant="outlined"
      sx={{
        // Fading is shown by going quiet rather than by a warning colour. A bug
        // nobody picked up is not an alarm, it is something receding, and dressing
        // it up as urgent is how a pool turns back into a nagging backlog.
        opacity: bug.isFading || bug.status === 'archived' ? 0.65 : 1,
        borderLeft: bug.disposition === 'emergency' ? 4 : 1,
        borderLeftColor: bug.disposition === 'emergency' ? 'error.main' : 'divider',
      }}
    >
      <CardContent sx={{ pb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 500,
                textDecoration: bug.status === 'fixed' ? 'line-through' : 'none',
              }}
            >
              {bug.title}
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 0.5 }}
            >
              <Typography variant="caption" color="text.secondary">
                Reported {describeAge(bug)}
                {bug.reporter?.name ? ` by ${bug.reporter.name}` : ''}
              </Typography>

              {bug.reportCount > 1 && (
                <Tooltip title="Reported more than once, which is the pool's own evidence that it matters">
                  <Chip size="small" label={`Seen ${bug.reportCount}×`} color="warning" variant="outlined" />
                </Tooltip>
              )}

              {bug.jiraKey && (
                <Link
                  href={bug.jiraUrl}
                  target="_blank"
                  rel="noopener"
                  variant="caption"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                >
                  {bug.jiraKey}
                  <OpenInNewIcon sx={{ fontSize: 12 }} />
                </Link>
              )}
            </Stack>
          </Box>

          {bug.assignee && (
            <Tooltip title={`${bug.assignee.name || bug.assignee.email}${mine ? ' (you)' : ''}`}>
              <Avatar
                sx={{ width: 28, height: 28, fontSize: 12, bgcolor: colorFor(bug.assignee.userId) }}
              >
                {initialsFor(bug.assignee.name || bug.assignee.email)}
              </Avatar>
            </Tooltip>
          )}

          {canEdit && (
            <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        {bug.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
            {bug.description}
          </Typography>
        )}

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          {decision && bug.disposition !== 'untriaged' && (
            <Chip
              size="small"
              icon={<decision.Icon fontSize="small" />}
              label={decision.label}
              color={decision.color === 'default' ? undefined : decision.color}
              variant={bug.disposition === 'emergency' ? 'filled' : 'outlined'}
            />
          )}

          {bug.status === 'fixed' && (
            <Chip
              size="small"
              icon={<CheckCircleOutlineIcon fontSize="small" />}
              color="success"
              variant="outlined"
              label={bug.fixedInCycle ? `Fixed in ${bug.fixedInCycle.name}` : 'Fixed'}
            />
          )}

          {bug.interruptedBet && (
            <Tooltip title="An emergency has to name what it stole time from. This is that bet.">
              <Chip
                size="small"
                icon={<LocalFireDepartmentIcon fontSize="small" />}
                color="error"
                variant="outlined"
                label={
                  bug.emergencyCostDays
                    ? `Cost ${bug.interruptedBet.name} ${bug.emergencyCostDays}d`
                    : `Interrupted ${bug.interruptedBet.name}`
                }
              />
            </Tooltip>
          )}

          {bug.pitch && (
            <Chip size="small" variant="outlined" label={`Pitch: ${bug.pitch.title}`} />
          )}

          {bug.isFading && (
            <Tooltip title="Nobody has picked this up. Sweeping it is safe: if it is real, it gets reported again.">
              <Chip size="small" variant="outlined" label="Fading" />
            </Tooltip>
          )}

          {isOpen && !bug.isFading && bug.daysUntilFade !== null && bug.daysUntilFade <= 7 && (
            <Typography variant="caption" color="text.secondary">
              Fades in {bug.daysUntilFade}d
            </Typography>
          )}
        </Stack>

        {bug.dispositionNote && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
            “{bug.dispositionNote}”
          </Typography>
        )}
      </CardContent>

      {canEdit && isOpen && (
        <>
          <Divider />
          <Box sx={{ px: 2, py: 1.25 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              {bug.disposition === 'untriaged' ? (
                <TriageBar
                  busy={busy}
                  showNote={false}
                  onDecide={(disposition, note) => onTriage?.(bug, disposition, note)}
                  onEmergency={() => onEmergency?.(bug)}
                />
              ) : (
                <>
                  {!bug.assignee && (
                    <Button
                      size="small"
                      startIcon={<PersonAddAltIcon fontSize="small" />}
                      disabled={busy}
                      onClick={() => onClaim?.(bug)}
                    >
                      Claim
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                    disabled={busy}
                    onClick={() => onFix?.(bug)}
                  >
                    Fixed
                  </Button>
                  <Button size="small" disabled={busy} onClick={() => setTriageOpen(o => !o)}>
                    Decide differently
                  </Button>
                </>
              )}
            </Stack>

            <Collapse in={triageOpen} unmountOnExit>
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Changing your mind is expected. A bug can move between decisions
                  freely — it just cannot go back to being undecided.
                </Typography>
                <TriageBar
                  current={bug.disposition}
                  busy={busy}
                  onDecide={(disposition, note) => {
                    setTriageOpen(false);
                    onTriage?.(bug, disposition, note);
                  }}
                  onEmergency={() => { setTriageOpen(false); onEmergency?.(bug); }}
                />
              </Box>
            </Collapse>
          </Box>
        </>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={act(onEdit)}>
          <EditOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={act(onReportAgain)}>
          <ReplayIcon fontSize="small" sx={{ mr: 1 }} /> It happened again
        </MenuItem>
        {isOpen && bug.disposition !== 'shape' && !bug.pitchId && (
          <MenuItem onClick={act(onEscalate)}>
            Too big — shape it into a pitch
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem onClick={act(onDelete)} sx={{ color: 'error.main' }}>
            Delete
          </MenuItem>
        )}
      </Menu>
    </Card>
  );
};

export default BugCard;
